import type { PendingUiReplayResponse } from '../domain/patientHost/types'
import type { ChatMessage } from '../types/chat'

/** Stable id — replaced on each replay attempt (not persisted to Redis chat). */
export const SESSION_PENDING_UI_REPLAY_DEBUG_MESSAGE_ID = 'voicnav:sessionPendingUiReplayDebug'

/** Result of ``POST /session/pending-ui-replay`` (and whether the widget ran the pipeline). */
export type PendingUiReplayOutcome = {
  pipelineRan: boolean
  pipelineSkipped?: boolean
  skipReason?: string
  httpOk: boolean
  httpStatus?: number
  replayed: boolean
  assistantRowCount: number
  showModalityRowCount: number
  attempt: number
}

function parseAssistantRowMetadata(raw: unknown): Record<string, unknown> | undefined {
  if (raw == null) return undefined
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw) as unknown
      return p && typeof p === 'object' && !Array.isArray(p) ? (p as Record<string, unknown>) : undefined
    } catch {
      return undefined
    }
  }
  return undefined
}

export function countShowModalityRowsInReplayMessages(rows: unknown[]): number {
  let n = 0
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const r = row as Record<string, unknown>
    if (String(r.role || '').toLowerCase() !== 'assistant') continue
    const md = parseAssistantRowMetadata(r.metadata)
    if (md?.kind === 'show_modality' && md.modality_found !== false) n++
  }
  return n
}

export function summarizePendingUiReplayResponse(
  replay: PendingUiReplayResponse | null,
  attempt: number,
): PendingUiReplayOutcome {
  if (replay == null) {
    return {
      pipelineRan: true,
      httpOk: false,
      replayed: false,
      assistantRowCount: 0,
      showModalityRowCount: 0,
      attempt,
      skipReason: 'HTTP request failed (network or non-2xx)',
    }
  }
  const rows = Array.isArray(replay.assistant_messages) ? replay.assistant_messages : []
  const httpOk = replay.httpOk !== false
  return {
    pipelineRan: true,
    httpOk,
    httpStatus: replay.httpStatus,
    replayed: Boolean(replay.replayed),
    assistantRowCount: rows.length,
    showModalityRowCount: countShowModalityRowsInReplayMessages(rows),
    attempt,
  }
}

export function buildPendingUiReplayResultChatText(outcome: PendingUiReplayOutcome): string {
  if (outcome.pipelineSkipped) {
    const why = (outcome.skipReason || 'no deferred modality state').trim()
    return `[Replay] POST /session/pending-ui-replay was not called — ${why}.`
  }
  const http = outcome.httpOk
    ? 'HTTP ok'
    : `HTTP failed${outcome.httpStatus != null ? ` (${outcome.httpStatus})` : ''}`
  const exec = outcome.replayed ? 'executing=yes' : 'executing=no'
  const rows = `${outcome.assistantRowCount} assistant row(s)`
  const show = `${outcome.showModalityRowCount} show_modality row(s) with file`
  let tail: string
  if (outcome.replayed && outcome.showModalityRowCount > 0) {
    tail = 'PDF/navigation should run from show_modality rows below.'
  } else if (outcome.replayed && outcome.assistantRowCount > 0) {
    tail =
      'Replay ran but no show_modality file row — often missing active_patient_id on session (name-only sync). PDF will not open.'
  } else if (outcome.replayed) {
    tail = 'Replay flag true but empty assistant_messages.'
  } else {
    tail =
      'Server did not run a deferred document (check session active patient vs target, deferred_open_ecg, or pending_ui_action).'
  }
  const attempt =
    outcome.attempt > 1 ? ` (attempt ${outcome.attempt})` : ''
  return `[Replay] POST /session/pending-ui-replay${attempt} — ${http}; ${exec}; ${rows}; ${show}. ${tail}`
}

/** Removes replay status bubble if present; does not add chat UI (replay still runs). */
export function appendPendingUiReplayDebugMessage(
  messages: readonly ChatMessage[],
  _outcome: PendingUiReplayOutcome,
): ChatMessage[] {
  return messages.filter((m) => m.id !== SESSION_PENDING_UI_REPLAY_DEBUG_MESSAGE_ID)
}
